import { flattenValues } from '@uniformdev/canvas';
import { UniformAdvocateHeadshot } from 'src/uniform/contenttypes/advocateHeadshot';
import { UniformLogo } from 'src/uniform/contenttypes/logo';

import {
	CustomerTestimonials,
	TestimonialProps,
} from '@/libraries/design/patterns/customer-testimonials/src/customer-testimonials';

// Uniform Customer Quote data structure (after flattenValues)
export interface UniformCustomerQuote {
	companyName: string;
	companyLogo: UniformLogo[];
	advocateName: string;
	advocateJobTitle: string;
	advocateHeadshot?: UniformAdvocateHeadshot[];
	quote: string;
	ctaUrl?: string;
	ctaLinkText?: string;
}

// Contentful data structure
interface ContentfulQuote {
	fields: {
		organization: {
			fields: {
				organizationNameExternal: string;
				logo: {
					fields: ContentfulImage;
				}[];
			};
		};
		advocateName: string;
		advocateJobTitle: string;
		advocateHeadshot: {
			fields: ContentfulImage;
		};
		advocateQuote: string;
		advocateCta: {
			fields: {
				ctaUrl: string;
				ctaButtonText: string;
			};
		}[];
	};
}

type ContentfulImage = {
	file: {
		contentType: string;
		url: string;
		details: {
			image: {
				width: number;
				height: number;
			};
		};
	};
};

// Raw Uniform CMS structure (before flattenValues)
export interface UniformQuoteRaw {
	type: 'customerQuote';
	_id: string;
	fields: {
		quote: { type: 'text'; value: string };
		companyLogo: { type: '$block'; value: unknown[] };
		companyName: { type: 'text'; value: string };
		advocateName: { type: 'text'; value: string };
		advocateJobTitle: { type: 'text'; value: string };
		advocateHeadshot?: { type: '$block'; value: unknown[] };
		ctaUrl?: { type: 'text'; value: string };
		ctaLinkText?: { type: 'text'; value: string };
	};
}

export type UniformCustomerTestimonialsProps = {
	theme: 'black' | 'white';
	quotes?: ContentfulQuote[];
	uniformQuotes?: UniformQuoteRaw[];
};

const transformContentfulImage = (image: ContentfulImage | undefined, alt = '') => {
	if (!image?.file?.url) {
		return undefined;
	}
	return {
		contentType: image.file.contentType || 'image',
		src: image.file.url,
		alt,
		baseWidth: 500,
		width: image.file.details?.image?.width,
		height: image.file.details?.image?.height,
	};
};

const transformUniformCompanyLogo = (logos: UniformLogo[] | undefined, alt = '') => {
	const logo = logos?.[0];
	if (!logo?.src) {
		return undefined;
	}
	return {
		contentType: 'image',
		src: logo.src,
		alt: logo.alt ?? alt,
		baseWidth: 500,
		width: logo.width,
		height: undefined,
	};
};

const transformUniformHeadshot = (headshots: UniformAdvocateHeadshot[] | undefined) => {
	const headshot = headshots?.[0];
	if (!headshot?.src) {
		return undefined;
	}
	return {
		contentType: 'image',
		src: headshot.src,
		alt: headshot.alt ?? '',
		baseWidth: 500,
		width: undefined,
		height: undefined,
	};
};

const transformCta = (url?: string, text?: string) => {
	if (!url || !text) {
		return undefined;
	}
	return { url, text };
};

const isContentfulQuote = (
	quote: ContentfulQuote | UniformCustomerQuote,
): quote is ContentfulQuote => {
	return 'fields' in quote && quote.fields !== undefined;
};

const transformContentfulQuote = (quote: ContentfulQuote): TestimonialProps => {
	const { fields } = quote;
	const cta = fields.advocateCta?.[0]?.fields;
	const companyName = fields.organization?.fields?.organizationNameExternal ?? '';

	return {
		logo: transformContentfulImage(
			fields.organization?.fields?.logo?.[0]?.fields as ContentfulImage,
			companyName,
		),
		name: fields.advocateName,
		title: fields.advocateJobTitle,
		image: transformContentfulImage(fields.advocateHeadshot?.fields),
		quote: fields.advocateQuote,
		link: transformCta(cta?.ctaUrl, cta?.ctaButtonText),
		label: companyName,
	} as TestimonialProps;
};

const transformUniformQuote = (quote: UniformCustomerQuote): TestimonialProps => {
	return {
		logo: transformUniformCompanyLogo(quote.companyLogo, quote.companyName),
		name: quote.advocateName,
		title: quote.advocateJobTitle,
		image: transformUniformHeadshot(quote.advocateHeadshot),
		quote: quote.quote,
		link: transformCta(quote.ctaUrl, quote.ctaLinkText),
		label: quote.companyName,
	} as TestimonialProps;
};

const isValidTestimonial = (quote: TestimonialProps): boolean => {
	return !!quote.logo && !!quote.name && !!quote.title && !!quote.image && !!quote.quote;
};

const flattenUniformQuote = (quote: UniformQuoteRaw): UniformCustomerQuote | null => {
	if (!quote || typeof quote !== 'object') {
		return null;
	}
	try {
		return flattenValues(quote) as UniformCustomerQuote;
	} catch {
		return null;
	}
};

const transformProps = (props: UniformCustomerTestimonialsProps) => {
	let testimonials: TestimonialProps[];

	if (props.uniformQuotes) {
		// Uniform CMS: flatten each raw quote entry, then transform
		const flattenedQuotes = (props.uniformQuotes ?? [])
			.map(flattenUniformQuote)
			.filter((quote): quote is UniformCustomerQuote => quote !== null);
		testimonials = flattenedQuotes.map(transformUniformQuote);
	} else {
		// Contentful fallback
		const contentfulQuotes = props.quotes ?? [];
		testimonials = contentfulQuotes
			.filter((quote): quote is ContentfulQuote => quote !== null && quote !== undefined)
			.map((quote) => {
				if (isContentfulQuote(quote)) {
					return transformContentfulQuote(quote);
				}
				return transformUniformQuote(quote as unknown as UniformCustomerQuote);
			});
	}

	return {
		bgColor: props.theme ?? 'white',
		testimonials: testimonials.filter(isValidTestimonial),
	};
};

const UniformCustomerTestimonials = (props: UniformCustomerTestimonialsProps) => {
	//TODO GWP-6632: add CTA links, and ensure they are localized in each panel

	const { bgColor, testimonials } = transformProps(props);

	return <CustomerTestimonials bgColor={bgColor} testimonials={testimonials} />;
};

export default UniformCustomerTestimonials;
